/**
 * ApplicantsTab — applicants panel + shared household section.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 *
 * NOTE: The useQuery here uses the same key as the header's applicants query in index.tsx.
 * TanStack Query deduplicates the network request when both enabled conditions are true.
 * The tab's isActive gate prevents the tab subscription from forcing a fresh fetch
 * until the tab is first opened; the header query may have already populated the cache.
 */

import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { badgeVariants } from '../../../components/ui/badge-variants';
import { Skeleton } from '../../../components/ui/skeleton';
import { Input } from '../../../components/ui/input';
import { formatCalendarDate } from '../../../utils/date';
import { asCalendarDateString } from '@resida/shared';
import {
  fetchApplicants,
  addApplicants,
  regenerateApplicantInvite,
  revokeApplicantInvite,
  withdrawApplicant,
} from '../../../lib/leasing.api';
import type {
  LeadDetail,
  Applicant,
  ApplicantRole,
  SharedApplicantData,
  ResidentialHistoryEntry,
  OtherIncomeEntry,
  RecurringDebtEntry,
  VehicleEntry,
  PersonalReferenceEntry,
  EmergencyContactEntry,
  CriminalDisclosureEntry,
} from './shared';
import {
  APPLICANT_ROLE_LABELS,
  APPLICANT_STATUS_BADGE,
  fmt,
  fmtCents,
  getPrimaryIncomeTypeLabel,
} from './shared-constants';

import { InfoRow } from './shared';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ApplicantsTabProps {
  lead: LeadDetail;
  onLeadUpdated: () => void;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// ApplicantDetailPanel (local)
// ---------------------------------------------------------------------------

interface ApplicantDetailPanelProps {
  applicant: Applicant;
}

function ApplicantDetailPanel({ applicant }: ApplicantDetailPanelProps) {
  const [open, setOpen] = useState(false);
  const { claimed } = applicant;

  // Only show if started or submitted
  if (applicant.status === 'invited' || applicant.status === 'withdrawn') {
    return null;
  }

  const residentialHistory = (claimed.claimedResidentialHistory as ResidentialHistoryEntry[] | null) ?? [];
  const otherIncomeSources = (claimed.claimedOtherIncomeSources as OtherIncomeEntry[] | null) ?? [];
  const recurringDebts = (claimed.claimedRecurringDebts as RecurringDebtEntry[] | null) ?? [];
  const vehicles = (claimed.claimedVehicles as VehicleEntry[] | null) ?? [];
  const personalReferences = (claimed.claimedPersonalReferences as PersonalReferenceEntry[] | null) ?? [];
  const emergencyContact = claimed.claimedEmergencyContact as EmergencyContactEntry | null | undefined;
  const criminalDisclosure = claimed.claimedCriminalDisclosure as CriminalDisclosureEntry | null | undefined;

  return (
    <div className="mt-2 border-t border-border pt-2">
      <button
        type="button"
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={`applicant-detail-${applicant.id}`}
      >
        <svg
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {open ? 'Hide' : 'View'} application data
      </button>

      {open && (
        <div
          id={`applicant-detail-${applicant.id}`}
          className="mt-3 space-y-4"
        >
          {/* Personal information */}
          <div data-testid="applicant-personal-section" className="bg-muted/20 rounded-lg border border-border p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Personal information</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <InfoRow label="Date of birth" value={formatCalendarDate(claimed.claimedDateOfBirth ? asCalendarDateString(claimed.claimedDateOfBirth.slice(0, 10)) : undefined)} />
              <InfoRow
                label="SSN last 4"
                value={claimed.claimedSsnLast4 ? `•••• ${claimed.claimedSsnLast4}` : '—'}
              />
              <InfoRow label="Household size (computed)" value={String(applicant.computedHouseholdSize ?? '—')} />
              {/* AP-002: claimedMoveInDate is a calendar date (@db.Date), not an instant. The API
                  serializes it via toISOString() ("YYYY-MM-DDT00:00:00.000Z"), so slice to the
                  YYYY-MM-DD calendar-date string before formatting — mirrors the fix already applied
                  to the same field in OverviewTab.tsx. Rendering it via fmtDate's
                  new Date(...).toLocaleDateString() shifted it a day backwards for any viewer west
                  of UTC. */}
              <InfoRow
                label="Desired move-in"
                value={formatCalendarDate(claimed.claimedMoveInDate ? asCalendarDateString(claimed.claimedMoveInDate.slice(0, 10)) : undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              />
            </div>
          </div>

          {/* Residential history */}
          <div data-testid="applicant-residential-section" className="bg-muted/20 rounded-lg border border-border p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Residential history</h4>
            {claimed.claimedFirstTimeRenter === true && (
              <p className="text-xs text-info mb-2">First-time renter — no prior address history.</p>
            )}
            {residentialHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not provided</p>
            ) : (
              <div className="space-y-3">
                {residentialHistory.map((r, i) => (
                  <div key={i} className="border-b border-border last:border-0 pb-3 last:pb-0">
                    <p className="text-sm font-medium text-foreground mb-1">
                      {[r.address, r.city, r.state, r.zipCode].filter(Boolean).join(', ') || '—'}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <InfoRow label="Landlord" value={fmt(r.landlordName)} />
                      <InfoRow label="Landlord phone" value={fmt(r.landlordPhone)} />
                      <InfoRow label="Monthly rent" value={fmtCents(r.monthlyRentCents)} />
                      <InfoRow label="Move-in" value={formatCalendarDate(r.moveInDate ? asCalendarDateString(r.moveInDate.slice(0, 10)) : undefined)} />
                      <InfoRow label="Move-out" value={formatCalendarDate(r.moveOutDate ? asCalendarDateString(r.moveOutDate.slice(0, 10)) : undefined)} />
                      <InfoRow label="Reason for leaving" value={fmt(r.reasonForLeaving)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Employment / Income */}
          <div data-testid="applicant-employment-section" className="bg-muted/20 rounded-lg border border-border p-4">
            {(() => {
              const incomeType = claimed.claimedPrimaryIncomeType
              const isNonEmployment = incomeType != null && incomeType !== 'employment' && incomeType !== 'self_employment'
              const sectionHeading = isNonEmployment ? 'INCOME' : 'EMPLOYMENT'
              return (
                <>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{sectionHeading}</h4>
                  {!isNonEmployment && <p className="text-xs font-medium text-muted-foreground mb-2">Current employer</p>}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    <InfoRow label="Primary income type" value={getPrimaryIncomeTypeLabel(incomeType)} />
                    {isNonEmployment ? (
                      <>
                        <InfoRow label="Income source" value={fmt(claimed.claimedEmployer)} />
                        <InfoRow label="Monthly income" value={fmtCents(claimed.claimedMonthlyIncomeCents)} />
                      </>
                    ) : (
                      <>
                        <InfoRow label="Employer" value={fmt(claimed.claimedEmployer)} />
                        <InfoRow label="Title / position" value={fmt(claimed.claimedPosition)} />
                        <InfoRow label="Start date" value={formatCalendarDate(claimed.claimedEmployerStartDate ? asCalendarDateString(claimed.claimedEmployerStartDate.slice(0, 10)) : undefined)} />
                        <InfoRow label="Monthly income" value={fmtCents(claimed.claimedMonthlyIncomeCents)} />
                        <InfoRow label="Supervisor" value={fmt(claimed.claimedSupervisorName)} />
                        <InfoRow label="Supervisor phone" value={fmt(claimed.claimedSupervisorPhone)} />
                      </>
                    )}
                  </div>
                </>
              )
            })()}
            {claimed.claimedPreviousEmployer && (
              <>
                <p className="text-xs font-medium text-muted-foreground mb-2">Previous employer</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <InfoRow label="Employer" value={fmt(claimed.claimedPreviousEmployer)} />
                  <InfoRow label="Title / position" value={fmt(claimed.claimedPreviousEmployerPosition)} />
                  <InfoRow label="Start date" value={formatCalendarDate(claimed.claimedPreviousEmployerStartDate ? asCalendarDateString(claimed.claimedPreviousEmployerStartDate.slice(0, 10)) : undefined)} />
                  <InfoRow label="End date" value={formatCalendarDate(claimed.claimedPreviousEmployerEndDate ? asCalendarDateString(claimed.claimedPreviousEmployerEndDate.slice(0, 10)) : undefined)} />
                  <InfoRow label="Monthly income" value={fmtCents(claimed.claimedPreviousEmployerMonthlyIncomeCents)} />
                </div>
              </>
            )}
          </div>

          {/* Additional income */}
          <div data-testid="applicant-other-income-section" className="bg-muted/20 rounded-lg border border-border p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Additional income</h4>
            {otherIncomeSources.length === 0 ? (
              <p className="text-sm text-muted-foreground">None disclosed</p>
            ) : (
              <ul className="space-y-1">
                {otherIncomeSources.map((inc, i) => (
                  <li key={i} className="flex justify-between text-sm">
                    <span className="text-foreground">{inc.type}</span>
                    <span className="text-muted-foreground">{fmtCents(inc.monthlyAmountCents)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Monthly debts */}
          <div data-testid="applicant-debts-section" className="bg-muted/20 rounded-lg border border-border p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Monthly debts</h4>
            {recurringDebts.length === 0 ? (
              <p className="text-sm text-muted-foreground">None disclosed</p>
            ) : (
              <ul className="space-y-1">
                {recurringDebts.map((debt, i) => (
                  <li key={i} className="flex justify-between text-sm">
                    <span className="text-foreground">{debt.type}</span>
                    <span className="text-muted-foreground">{fmtCents(debt.monthlyPaymentCents)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Vehicles */}
          <div data-testid="applicant-vehicles-section" className="bg-muted/20 rounded-lg border border-border p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Vehicles</h4>
            {vehicles.length === 0 ? (
              <p className="text-sm text-muted-foreground">None disclosed</p>
            ) : (
              <ul className="space-y-1">
                {vehicles.map((v, i) => (
                  <li key={i} className="text-sm text-foreground">
                    {[v.year, v.make, v.model].filter(Boolean).join(' ') || '—'}
                    {v.licensePlate ? ` — ${v.licensePlate}${v.state ? ` (${v.state})` : ''}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Personal references */}
          <div data-testid="applicant-references-section" className="bg-muted/20 rounded-lg border border-border p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Personal references</h4>
            {personalReferences.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not provided</p>
            ) : (
              <div className="space-y-3">
                {personalReferences.map((ref, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <InfoRow label="Name" value={fmt(ref.name)} />
                    <InfoRow label="Relationship" value={fmt(ref.relationship)} />
                    <InfoRow label="Phone" value={fmt(ref.phone)} />
                    <InfoRow label="Email" value={fmt(ref.email)} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Emergency contact */}
          <div data-testid="applicant-emergency-section" className="bg-muted/20 rounded-lg border border-border p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Emergency contact</h4>
            {!emergencyContact ? (
              <p className="text-sm text-muted-foreground">Not provided</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <InfoRow label="Name" value={fmt(emergencyContact.name)} />
                <InfoRow label="Relationship" value={fmt(emergencyContact.relationship)} />
                <InfoRow label="Phone" value={fmt(emergencyContact.phone)} />
              </div>
            )}
          </div>

          {/* Criminal history disclosure */}
          <div data-testid="applicant-criminal-section" className="bg-muted/20 rounded-lg border border-border p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Criminal history disclosure</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Fair-housing notice: A conviction does not automatically disqualify the applicant.
              Each application is reviewed individually.
            </p>
            {criminalDisclosure ? (
              <div data-testid="applicant-criminal-disclosure" className="space-y-2">
                <div>
                  <span className="text-xs font-medium text-muted-foreground">Felony conviction: </span>
                  <span className={`text-sm font-medium ${criminalDisclosure.hasFelony ? 'text-warning-foreground' : 'text-foreground'}`}>
                    {criminalDisclosure.hasFelony ? 'Yes' : 'No'}
                  </span>
                  {criminalDisclosure.hasFelony && criminalDisclosure.felonyExplanation && (
                    <p className="text-sm text-muted-foreground mt-1 pl-2 border-l-2 border-border">
                      {criminalDisclosure.felonyExplanation}
                    </p>
                  )}
                </div>
                <div>
                  <span className="text-xs font-medium text-muted-foreground">Misdemeanor/other conviction: </span>
                  <span className={`text-sm font-medium ${criminalDisclosure.hasMisdemeanor ? 'text-warning-foreground' : 'text-foreground'}`}>
                    {criminalDisclosure.hasMisdemeanor ? 'Yes' : 'No'}
                  </span>
                  {criminalDisclosure.hasMisdemeanor && criminalDisclosure.misdemeanorExplanation && (
                    <p className="text-sm text-muted-foreground mt-1 pl-2 border-l-2 border-border">
                      {criminalDisclosure.misdemeanorExplanation}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Not yet disclosed</p>
            )}
          </div>

          {/* Inquiry notes (legacy free-text pets field from iteration 1) */}
          {claimed.claimedPets && (
            <div className="bg-muted/20 rounded-lg border border-border p-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Pets (legacy note)</h4>
              <p className="text-sm text-muted-foreground">{claimed.claimedPets}</p>
            </div>
          )}

          {/* Notes */}
          {claimed.claimedNotes && (
            <div className="bg-muted/20 rounded-lg border border-border p-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Additional notes</h4>
              <p className="text-sm text-foreground whitespace-pre-wrap">{claimed.claimedNotes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SharedHouseholdSection (local)
// ---------------------------------------------------------------------------

interface SharedHouseholdSectionProps {
  sharedData: SharedApplicantData | null | undefined;
}

function SharedHouseholdSection({ sharedData }: SharedHouseholdSectionProps) {
  if (!sharedData) return null;

  const pets = (sharedData.applicationPets ?? []);
  const occupants = (sharedData.applicationOccupants ?? []);

  return (
    <div data-testid="applicant-shared-household" className="bg-card rounded-lg border border-border p-5">
      <h3 className="text-sm font-semibold text-muted-foreground mb-1">Shared household data</h3>
      <p className="text-xs text-muted-foreground mb-4">Shared by all applicants on this lead.</p>

      <div className="mb-4">
        <h4 className="text-xs font-medium text-muted-foreground mb-2">Pets</h4>
        {pets.length === 0 ? (
          <p className="text-sm text-muted-foreground">None</p>
        ) : (
          <ul className="space-y-1">
            {pets.map((p, i) => (
              <li key={i} className="text-sm text-foreground">
                {p.type.charAt(0).toUpperCase() + p.type.slice(1)}{p.name ? ` — ${p.name}` : ''}{p.breed ? ` (${p.breed})` : ''}{p.weightLbs ? ` · ${p.weightLbs} lbs` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="text-xs font-medium text-muted-foreground mb-2">Occupants / dependents</h4>
        {occupants.length === 0 ? (
          <p className="text-sm text-muted-foreground">None</p>
        ) : (
          <ul className="space-y-1">
            {occupants.map((o, i) => (
              <li key={i} className="text-sm text-foreground">
                {o.name} ({o.relationship}){o.dateOfBirth ? ` — DOB ${formatCalendarDate(asCalendarDateString(o.dateOfBirth.slice(0, 10)))}` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ApplicantsPanel (local)
// ---------------------------------------------------------------------------

interface ApplicantsPanelProps {
  lead: LeadDetail;
  onUpdated: () => void;
  isActive: boolean;
}

function ApplicantsPanel({ lead, onUpdated, isActive }: ApplicantsPanelProps) {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState<ApplicantRole>('primary');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [regenLink, setRegenLink] = useState<{ applicantId: string; tokenUrl: string; expiresAt: string } | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
  const [regenCopied, setRegenCopied] = useState(false);

  const inputCls = 'w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring';

  // NOTE: Same query key as the header's applicants query in index.tsx.
  // TanStack Query deduplicates when both enabled conditions are true.
  // The isActive gate ensures this tab doesn't force a fresh fetch until opened.
  const { data: applicantsData, isLoading, error } = useQuery({
    queryKey: ['applicants', lead.id],
    queryFn: () => fetchApplicants(lead.id),
    enabled: isActive,
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['applicants', lead.id] });
  }, [queryClient, lead.id]);

  const applicants = applicantsData ?? [];

  const handleAddApplicant = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    setIsAdding(true);
    try {
      await addApplicants(lead.id, [{ name: addName.trim(), email: addEmail.trim(), role: addRole }]);
      setAddName('');
      setAddEmail('');
      setAddRole('primary');
      setShowAddForm(false);
      invalidate();
      onUpdated();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add applicant');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRegenerateInvite = async (applicantId: string) => {
    setPanelError(null);
    try {
      const result = await regenerateApplicantInvite(lead.id, applicantId);
      setRegenLink({ applicantId, tokenUrl: result.tokenUrl, expiresAt: result.expiresAt });
      setRegenCopied(false);
      invalidate();
    } catch (err) {
      setPanelError(err instanceof Error ? err.message : 'Failed to regenerate invite');
    }
  };

  const handleCopyRegenLink = async () => {
    if (!regenLink) return;
    try {
      await navigator.clipboard.writeText(regenLink.tokenUrl);
      setRegenCopied(true);
      setTimeout(() => setRegenCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleRevokeInvite = async (applicantId: string) => {
    if (!window.confirm('Revoke this applicant\'s invite link? They will no longer be able to access the portal with the current link.')) return;
    setRevokingId(applicantId);
    setPanelError(null);
    try {
      await revokeApplicantInvite(lead.id, applicantId);
      invalidate();
    } catch (err) {
      setPanelError(err instanceof Error ? err.message : 'Failed to revoke invite');
    } finally {
      setRevokingId(null);
    }
  };

  const handleWithdraw = async (applicantId: string, name: string) => {
    if (!window.confirm(`Withdraw ${name} from the application? This action marks the applicant as withdrawn.`)) return;
    setWithdrawingId(applicantId);
    setPanelError(null);
    try {
      await withdrawApplicant(lead.id, applicantId);
      invalidate();
      onUpdated();
    } catch (err) {
      setPanelError(err instanceof Error ? err.message : 'Failed to withdraw applicant');
    } finally {
      setWithdrawingId(null);
    }
  };

  return (
    <section
      data-testid="applicants-panel"
      className="mb-6 rounded-lg border border-border bg-card p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">Applicants</h2>
        {!showAddForm && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="add-applicants-btn"
            onClick={() => setShowAddForm(true)}
            aria-label="Add an applicant to this lead"
          >
            + Add applicant
          </Button>
        )}
      </div>

      {panelError && (
        <div
          className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
          data-testid="applicants-panel-error"
        >
          {panelError}
        </div>
      )}

      {isLoading && <Skeleton className="h-16 w-full" />}

      {error && (
        <div
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
        >
          Failed to load applicants. Please refresh.
        </div>
      )}

      {!isLoading && !error && applicants.length === 0 && !showAddForm && (
        <p className="text-sm text-muted-foreground italic">No applicants added yet.</p>
      )}

      {!isLoading && applicants.length > 0 && (
        <ul className="space-y-3 mb-4">
          {applicants.map((applicant) => (
            <li
              key={applicant.id}
              data-testid={`applicant-row-${applicant.id}`}
              className="flex flex-col gap-2 rounded-lg border border-border p-3"
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-sm font-medium">{applicant.name}</p>
                  <p className="text-xs text-muted-foreground">{applicant.email}</p>
                  <p className="text-xs text-muted-foreground">{APPLICANT_ROLE_LABELS[applicant.role]}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    data-testid={`applicant-status-${applicant.id}`}
                    className={badgeVariants({ variant: APPLICANT_STATUS_BADGE[applicant.status] ?? 'secondary' })}
                  >
                    {applicant.status.charAt(0).toUpperCase() + applicant.status.slice(1)}
                  </span>
                  {applicant.submittedAt && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(applicant.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>

              {/* Invite token info */}
              {applicant.activeInviteToken.hasToken && applicant.status !== 'withdrawn' && (
                <p className="text-xs text-muted-foreground">
                  Invite expires:{' '}
                  {applicant.activeInviteToken.expiresAt
                    ? new Date(applicant.activeInviteToken.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'unknown'}
                </p>
              )}

              {/* Regen link display */}
              {regenLink?.applicantId === applicant.id && (
                <div className="flex gap-2 mt-1">
                  <Input
                    type="text"
                    readOnly
                    value={regenLink.tokenUrl}
                    className="flex-1 bg-muted/40 border-border px-2 py-1 font-mono min-w-0 md:text-xs"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                    aria-label="Regenerated invite URL"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void handleCopyRegenLink()}
                    aria-label={regenCopied ? 'Link copied' : 'Copy invite link'}
                    className="shrink-0 text-xs"
                  >
                    {regenCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid={`applicant-regenerate-invite-${applicant.id}`}
                  onClick={() => void handleRegenerateInvite(applicant.id)}
                  aria-label={applicant.status === 'withdrawn' ? `Reinstate ${applicant.name}` : `Regenerate invite for ${applicant.name}`}
                  className="text-xs"
                >
                  {applicant.status === 'withdrawn' ? 'Reinstate' : 'Regenerate invite'}
                </Button>
                {applicant.activeInviteToken.hasToken && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`applicant-revoke-invite-${applicant.id}`}
                    disabled={revokingId === applicant.id}
                    onClick={() => void handleRevokeInvite(applicant.id)}
                    aria-label={`Revoke invite for ${applicant.name}`}
                    className="text-xs"
                  >
                    {revokingId === applicant.id ? 'Revoking…' : 'Revoke invite'}
                  </Button>
                )}
                {applicant.status !== 'withdrawn' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`applicant-withdraw-${applicant.id}`}
                    disabled={withdrawingId === applicant.id}
                    onClick={() => void handleWithdraw(applicant.id, applicant.name)}
                    aria-label={`Withdraw ${applicant.name} from application`}
                    className="text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
                  >
                    {withdrawingId === applicant.id ? 'Withdrawing…' : 'Withdraw'}
                  </Button>
                )}
              </div>

              {/* Application data */}
              <ApplicantDetailPanel applicant={applicant} />
            </li>
          ))}
        </ul>
      )}

      {/* Shared household data (shown once per lead) */}
      {!isLoading && !error && applicants.length > 0 && (() => {
        const shared = applicants.find((a) => a.sharedData != null)?.sharedData;
        return shared ? <SharedHouseholdSection sharedData={shared} /> : null;
      })()}

      {/* Add applicant form */}
      {showAddForm && (
        <form
          data-testid="add-applicants-form"
          onSubmit={(e) => void handleAddApplicant(e)}
          className="space-y-3 mt-4 border border-border rounded-lg p-4"
        >
          <h3 className="text-sm font-semibold">Add applicant</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="applicant-name" className="block text-xs font-medium mb-1">
                Name <span className="text-destructive" aria-hidden="true">*</span>
              </label>
              <input
                id="applicant-name"
                type="text"
                required
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="Full name"
                className={inputCls}
                aria-required="true"
              />
            </div>
            <div>
              <label htmlFor="applicant-email" className="block text-xs font-medium mb-1">
                Email <span className="text-destructive" aria-hidden="true">*</span>
              </label>
              <input
                id="applicant-email"
                data-testid="applicant-email-input"
                type="email"
                required
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="applicant@example.com"
                className={inputCls}
                aria-required="true"
              />
            </div>
          </div>
          <div>
            <label htmlFor="applicant-role" className="block text-xs font-medium mb-1">
              Role <span className="text-destructive" aria-hidden="true">*</span>
            </label>
            <select
              id="applicant-role"
              value={addRole}
              onChange={(e) => setAddRole(e.target.value as ApplicantRole)}
              className={inputCls}
              required
              aria-required="true"
            >
              <option value="primary">Primary applicant</option>
              <option value="co_applicant">Co-applicant</option>
              <option value="guarantor">Guarantor</option>
            </select>
          </div>
          {addError && (
            <div
              className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
              role="alert"
            >
              {addError}
            </div>
          )}
          <div className="flex gap-3">
            <Button
              type="submit"
              size="sm"
              disabled={isAdding}
              data-testid="add-applicants-submit"
              aria-label="Add applicant to this lead"
            >
              {isAdding ? 'Adding…' : 'Add applicant'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => { setShowAddForm(false); setAddError(null); }}
              disabled={isAdding}
              aria-label="Cancel adding applicant"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// ApplicantsTab
// ---------------------------------------------------------------------------

export function ApplicantsTab({ lead, onLeadUpdated, isActive }: ApplicantsTabProps) {
  return (
    <div>
      <ApplicantsPanel lead={lead} onUpdated={onLeadUpdated} isActive={isActive} />
    </div>
  );
}
